import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Developer, Program, Programs, SiteTopology} from '../../domain/cloud-topology';
import { Router } from '@angular/router';
import {NgbAccordionItem} from '@ng-bootstrap/ng-bootstrap';
import {TopologyService} from '../../services/topology/topology.service';
import {SideNavService} from '../../services/side-nav.service';
import {ProgramService} from '../../services/vpp/program.service';
import {forkJoin } from 'rxjs';
import {FormControl} from '@angular/forms';
import {CookieService} from 'ngx-cookie-service';
import {MatDrawer} from '@angular/material/sidenav';
import {NodeDefinition, TreeNode} from 'tree';

interface ElementNode {
  name: string;
  id: string;
  children?: ElementNode[];
  url?: string;
  selected: boolean;
  icon?: any;
  type: string;
}

@Component({
    selector: 'app-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['./side-nav.component.scss'],
    standalone: false
})
export class SideNavComponent implements OnInit {

  constructor(private router: Router,
              private topologyService: TopologyService,
              private sideNavService: SideNavService,
              private programService: ProgramService,
              private cookieService: CookieService) {}
  @Input() isCollapsed: boolean;
  @Input() navComponents: string[];
  @Input() appName: string;
  @Input() drawer: MatDrawer;
  @Input() isMenuOpen = true;
  @Output() isMenuOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('sideNav') sideNav: NgbAccordionItem;
  searchControl = new FormControl();

  selectedComponent = 'site';
  developers: Developer[];
  sites: SiteTopology[] = [];
  programs: Program[] = [];
  requestsCompleted: boolean;
  TREE_DATA: ElementNode[] = [];
  searchOptions: string[] = [];
  displayProperty = 'name';
  showSearch = true;
  showControls = false;
  expandedNodes: Set<TreeNode<ElementNode>> = new Set<TreeNode<ElementNode>>();

  selectedSiteNodes: Set<TreeNode<ElementNode>> = new Set<TreeNode<ElementNode>>();
  selectedProgramNodes: Set<TreeNode<ElementNode>> = new Set<TreeNode<ElementNode>>();

  treeHeight = '0px';

  iconMap: { [key: string]: string } = {
    'apartment': 'apartment',
    'briefcase': 'briefcase',
    'universal-access': 'universal-access',
  };

  placeholderNodeAttribute = 'type';
  placeholderNodeValues: string[] = ['parent', 'developer', 'account'];
  placeholderNodeDefinitions: NodeDefinition = { [this.placeholderNodeAttribute]: this.placeholderNodeValues };

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.calculateHeight();
  }

  ngOnInit() {
    if (this.cookieService.get('selectedComponent')) {
      this.selectedComponent = this.cookieService.get('selectedComponent');
    } else {
      this.selectedComponent = 'program';
    }

    this.requestsCompleted = false;
    this.selectedSiteNodes.clear();
    this.selectedProgramNodes.clear();
    forkJoin([
      this.topologyService.getTopology(),
      this.programService.getPrograms()
    ]).subscribe(data => {
      const sites = data[0];
      const programsData = data[1];
      this.sites = data[0];
      this.programs = data[1];
      const routerUrl = this.router.url;

      this.sideNavService.setSiteTopology(sites);

      const siteParent: ElementNode = {
        id: 'site-parent',
        name: 'DER.OS',
        children: [],
        selected: false,
        url: null,
        type: 'parent'
      };

      const developers = this.sideNavService.getDevelopers();

      for (const developer of developers) {
        const developerNode: ElementNode = {
          name: developer.name,
          id: developer.name,
          children: [],
          url: null,
          type: 'developer',
          selected: false,
          icon: 'universal-access',
        };
        const developerAccounts = this.sideNavService.getAccountsForDeveloper(developer.id);
        for (const account of developerAccounts) {
          const accountNode: ElementNode = {
            name: account.name,
            id: account.id,
            children: [],
            url: null,
            selected: false,
            icon: 'briefcase',
            type: 'account'
          };
          const topologySites = this.sideNavService.getSitesForAccount(account.id);

          for (const site of topologySites) {
            const siteUrl = `/site/${site.id}/home`;
            const siteNode = {
              name: site.name,
              id: site.id,
              url: siteUrl,
              selected: false,
              icon: 'apartment',
              type: 'site'
            };
            if (routerUrl.includes(siteNode.url)) {
              this.selectedSiteNodes.add(siteNode);
            }
            accountNode.children.push(siteNode);
          }
          developerNode.children.push(accountNode);
        }
        siteParent.children.push(developerNode);
      }

      const programs = Programs.sortAlphabetically(programsData);
      this.sideNavService.setProgramsCache(programs);
      programs.unshift({
        name: 'Summary View',
        id: 'home',
        programType: 'home',
        properties: {
          programPenalty: 0,
          performanceTarget: '',
          marketAuction: false,
          notificationTimeframe: 0,
          programIncentive: 0,
          pointOfMeasurement: '',
          nominatedKw: 0,
          forwardStats: false,
          forceMaxDispatch: false
        }
      });
      if (this.selectedComponent === 'program') {
        const pro = programs.find(program => {
          return routerUrl.includes(program.id);
        });
        this.selectedProgramNodes.add(pro);
      }

      this.expandedNodes.add(siteParent);
      this.TREE_DATA.push(siteParent);

      this.updateNavigation();
      this.requestsCompleted = true;
      this.calculateHeight();
      window.dispatchEvent(new Event('resize'));
    });

  }

  selectNode(nodes: TreeNode<any>[]) {
    const node = nodes[0];
    if (this.selectedComponent === 'program') {
      if (node.id === 'home') {
        this.router.navigate(['program', 'home']);
      } else {
        this.router.navigate(['program', node.id , 'view']);
      }
    } else if (this.selectedComponent === 'site') {
      this.router.navigate(['site', node.id, 'home']);
    }
  }

  calculateHeight(): void {
    const globalNavHeight = document.querySelector('global-nav').clientHeight;
    const sidenavHeaderHeight =
      document.querySelector('.sidenav-header').clientHeight;
    this.treeHeight = `${
      window.innerHeight -
      globalNavHeight -
      sidenavHeaderHeight - 20
    }px`;
  }

  isControlSelected() {
    return this.router.url.includes('control');
  }

  onToolbarMenuToggle() {
    this.isMenuOpen = !this.isMenuOpen;
    this.isMenuOpenChange.emit(this.isMenuOpen);
  }

  updateNavigation() {
    this.searchOptions = [];
    let navigationArgs = [];
    this.searchControl.setValue('');

    if (this.router.url.includes('site') && this.cookieService.get('selectedComponent') !== 'site') {
      this.selectedComponent = 'site';
    }

    if (this.sites && this.selectedComponent === 'site') {
      this.sites.forEach( (site) => {
        this.searchOptions.push(site.name);
      });
    }

    if (this.programs && this.selectedComponent === 'program') {
      this.programs.forEach( (program) => {
        this.searchOptions.push(program.name);
      });

      if (!this.router.url.includes('view')) {
        navigationArgs = ['program', 'home'];
      }
    }

    if (this.selectedComponent === 'control') {
      this.cookieService.set('selectedComponent', 'control');
      navigationArgs = ['control', 'home'];
    }
    this.cookieService.set('selectedComponent', this.selectedComponent, 1, '/', document.location.hostname, false, 'Strict');
    if (navigationArgs.length > 0) {
      this.router.navigate(navigationArgs).then(() => {});
    }
  }
}


